/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  JobElementTotal,
  JobElementType,
  isJobElementTotal,
  BuilderUserContext,
  FilterNames,
  FilterRestService,
} from '@gh/builder-shared';
import { BuilderPermission, JobStatus, SystemRoleBit } from '@gh/builder-shared-data';
import {
  AppRouter, DateTimeTypes,
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getRouteInitData,
  NumberTypes,
} from '@gh/common-shared';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import { NotificationService } from '@gh/core-ui';
import {
  buildDateTimeColumn,
  buildNumberColumnLeft,
  cursorTracker,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';
import { JobListFilter, JobListItem } from '../../resources/job.model';
import { JobNavigationService } from '../../resources/job-navigation.service';
import { JobFinancialsListBusinessService } from '../../resources/job-financials-list-business.service';
import { TranslationService } from '@gh/core-mls';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  conditionalRowPostCreateDecorator,
  GridSettingsService,
  textOverlayRowPostCreate,
} from '@gh/common-internal-shared';

@Component({
 selector: 'gh-job-financials-list',
 template: require('./job-financials-list.component.html'),
 styles: [require('./job-financials-list.component.scss')],
})
export class JobFinancialsListComponent implements OnInit, OnDestroy {
  dataSource: SlicedDataSource<JobListItem>;
  form: FilterableFormState<JobListFilter>;
  gridDefinition: LayoutBasedGridDefinition;
  cursor = newGridPaginationCursor();
  total$: Observable<any>;
  _total  = new BehaviorSubject<JobElementTotal[]>([{type: JobElementType.Total, id: 'total-count'}]);

  get havePermissions(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.CREATE_EDIT)
      || this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.VIEW);
  }

  constructor(private route: ActivatedRoute,
              private routeData: RouteDataService,
              private userContext: BuilderUserContext,
              private formStateBuilder: FormStateBuilder,
              private businessService: JobFinancialsListBusinessService,
              private notifications: NotificationService,
              private router: AppRouter,
              private translations: TranslationService,
              private jobNavigation: JobNavigationService,
              private gridService: GridSettingsService,
              private filterService: FilterRestService,
              public gridDefinitionFactory: GridDefinitionFactory,
             ) {
    this.total$ = this._total.asObservable();
    this.gridService.setCurrentTable('jobFinancialsList');
  }

  isJobDpp(job: JobListItem): boolean {
    return job.isDpp;
  }

  ngOnInit(): void {
    const {filter: initFilter, gridLayout} = getRouteInitData(this.route);
    let isInit = true;
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        jobNumber: {
          // list always gets sorted from BE
          comparator: () => 0,
        },
        drawsMade: buildNumberColumnLeft(NumberTypes.Price),
        contractAmountWithGst: buildNumberColumnLeft(NumberTypes.Price),
        variationsAmountWithGst: buildNumberColumnLeft(NumberTypes.Price),
        totalAmount: buildNumberColumnLeft(NumberTypes.Price),
        totalAmountWithGst: buildNumberColumnLeft(NumberTypes.Price),
        estimatedProfit: buildNumberColumnLeft(NumberTypes.Price),
        profitMargin: buildNumberColumnLeft(NumberTypes.Price),
        originalBudget: buildNumberColumnLeft(NumberTypes.Price),
        originalContractAmount: buildNumberColumnLeft(NumberTypes.Price),
        originalProfitMargin: buildNumberColumnLeft(NumberTypes.Price),
        currentBudget: buildNumberColumnLeft(NumberTypes.Price),
        totalInvoices: buildNumberColumnLeft(NumberTypes.Price),
        drawsMadeWithGst: buildNumberColumnLeft(NumberTypes.Price),
        actualProfit: buildNumberColumnLeft(NumberTypes.Price),

        status: {
          cellClass: ({data}: { data: any }) => ({
            [JobStatus.Dpp]: 'cell--important',
            [JobStatus.NotProceeding]: 'cell--negative',
            [JobStatus.Active]: 'cell--positive',
          }[data.statusValue]),
        },
        finalizedDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        lastAccountingDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        finalClaimDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        handoverDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
      },
      {
        defaultColDef: {
          comparator: () => 0,
        },
        getRowNodeId: (item: JobListItem) => item.id,
        processRowPostCreate: conditionalRowPostCreateDecorator(
          ({ node: { data } }) => isJobElementTotal(data),
          textOverlayRowPostCreate(this.translations.get('lbl_total').toUpperCase())),
      });
    this.gridDefinition.bind(gridLayout);

    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: initFilter,
      children: {
        searchString: void 0,
        showFinalized: void 0,
        showDpp: void 0,
        showActive: void 0,
        showUserAssignedJobs: void 0,
        showNotProceeding: void 0,
      },
    }));

    const tracker = this.routeData.tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      this.dataSource = this.businessService.findAllAsDataSource({
        ...filter,
      });
      if (!isInit) {
        this.filterService.update(FilterNames.jobFinancialsList, filter).subscribe();
      }
      isInit = false;
    });
  }

  ngOnDestroy(): void {
    this.gridService.leaveTable();
  }

  onJobDetailsOpen(job: JobListItem): void {
    this.jobNavigation.onJobDetailsOpen(job, 'lbl_job_financials');
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  onJobSpecificationOpen(job: JobListItem): void {
    this.jobNavigation.onJobSpecificationOpen(job, 'lbl_job_financials');
  }

  onColorSelectionOpen(job: JobListItem): void {
    this.jobNavigation.onColorSelectionOpen(job, 'lbl_job_financials');
  }

  onJobEstimateOpen(job: JobListItem): void {
    this.jobNavigation.onJobEstimateOpen(job, 'lbl_job_financials');
  }

  onFinancialsOpen(job: JobListItem): void {
    if (!this.isElement(job)) {
      return;
    }
    this.jobNavigation.onFinancialsOpen(job, 'lbl_job_financials');
  }

  onJobScheduleOpen(): void {
    // not implemented yet
  }

  onJobOrderOpen(job: JobListItem): void {
    this.jobNavigation.onJobOrderOpen(job, 'lbl_job_financials');
  }

  onJobChangeOrderOpen(job: JobListItem): void {
    this.jobNavigation.onJobChangeOrderOpen(job, 'lbl_job_financials');
  }

  onLoadData(event: any): void {
    const totals = event.totals;
    const total = {};
    for (const key in totals) {
      if (totals.hasOwnProperty(key)) {
        total[key] = totals[key];
      }
    }
    this._total.next([{type: JobElementType.Total, id: 'total-count', variationId: 'variation:footer', ...total  }]);
  }

  isElement(element: any): boolean {
    return element.type !== JobElementType.Total;
  }
}
